import React, { ReactNode } from 'react';
import { ColumnsType } from 'antd/lib/table';
import {
  OptionProps,
  ReportsFilters,
  ReportsSortBy,
  StructuralPassFailRow,
} from '@models/interfaces';
import {
  getStructuralStatus as deriveStructuralStatus,
  getStructuralStatusClassName as getDerivedStructuralStatusClassName,
  getStructuralStatusLabel as getDerivedStructuralStatusLabel,
} from '../structural-pass-fail-helpers';
import {
  getStructuralSortableColumn,
  renderBooleanValue,
  renderPercentValue,
  renderValue,
} from './common';

const renderStructuralStatus = (
  _value: unknown,
  record: StructuralPassFailRow,
): ReactNode => {
  const status = deriveStructuralStatus(record);

  return React.createElement(
    'span',
    { className: getDerivedStructuralStatusClassName(status) },
    getDerivedStructuralStatusLabel(status),
  );
};

export const getStructuralPassFailColumns = (
  filters: ReportsFilters,
  sortByOptions: OptionProps[],
): ColumnsType<StructuralPassFailRow> => [
  {
    key: 'structuralStatus',
    render: renderStructuralStatus,
    title: 'Structural Status',
  },
  getStructuralSortableColumn(
    'projectBundleID',
    'Project Bundle ID',
    ReportsSortBy.ProjectBundleID,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'fuzeIDsDisplay',
    key: 'fuzeIDsDisplay',
    render: renderValue,
    title: 'Fuze IDs',
  },
  {
    dataIndex: 'psProjectNumbersDisplay',
    key: 'psProjectNumbersDisplay',
    render: renderValue,
    title: 'PS Project Numbers',
  },
  getStructuralSortableColumn(
    'projectType',
    'Project Type',
    ReportsSortBy.ProjectType,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'projectSubtype',
    key: 'projectSubtype',
    render: renderValue,
    title: 'Project Subtype',
  },
  getStructuralSortableColumn(
    'projectStatus',
    'Project Status',
    ReportsSortBy.ProjectStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getStructuralSortableColumn(
    'progressStatus',
    'Progress Status',
    ReportsSortBy.ProgressStatus,
    filters,
    sortByOptions,
    renderValue,
  ),
  getStructuralSortableColumn(
    'siteName',
    'Site Name',
    ReportsSortBy.SiteName,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'psLocationCode',
    key: 'psLocationCode',
    render: renderValue,
    title: 'PS Location Code',
  },
  {
    dataIndex: 'mdgLocationCode',
    key: 'mdgLocationCode',
    render: renderValue,
    title: 'MDG Location Code',
  },
  {
    dataIndex: 'city',
    key: 'city',
    render: renderValue,
    title: 'City',
  },
  {
    dataIndex: 'state',
    key: 'state',
    render: renderValue,
    title: 'State',
  },
  {
    dataIndex: 'submarket',
    key: 'submarket',
    render: renderValue,
    title: 'Submarket',
  },
  {
    dataIndex: 'towerOwner',
    key: 'towerOwner',
    render: renderValue,
    title: 'Tower Owner',
  },
  {
    dataIndex: 'structuralRequired',
    key: 'structuralRequired',
    render: renderBooleanValue,
    title: 'Structural Required',
  },
  getStructuralSortableColumn(
    'structuralResult',
    'Structural Result',
    ReportsSortBy.StructuralResult,
    filters,
    sortByOptions,
    renderValue,
  ),
  {
    dataIndex: 'structuralAdequate',
    key: 'structuralAdequate',
    render: renderBooleanValue,
    title: 'Structural Adequate',
  },
  getStructuralSortableColumn(
    'towerPercent',
    'Tower %',
    ReportsSortBy.TowerPercent,
    filters,
    sortByOptions,
    renderPercentValue,
  ),
  getStructuralSortableColumn(
    'foundationPercent',
    'Foundation %',
    ReportsSortBy.FoundationPercent,
    filters,
    sortByOptions,
    renderPercentValue,
  ),
];

export const getStructuralPassFailReportRowClassName = (
  record: StructuralPassFailRow,
): string =>
  record.projectBundleID
    ? 'prov-structural-pass-fail-report__row--clickable'
    : 'prov-structural-pass-fail-report__row--disabled';

export const getStructuralPassFailReportRowKey = (
  record: StructuralPassFailRow,
): string =>
  [record.projectId, record.projectBundleID].filter(Boolean).join('-');
